import React, { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Calendar, AlertTriangle, Activity, Loader2 } from "lucide-react";
import {
  dashboardService,
  WorkflowExecutionDataPoint,
} from "../services/dashboard.service";

interface ChartDataPoint {
  date: string;
  done: number;
  failed: number;
  total: number;
}

const WorkflowExecutionChart: React.FC = () => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardService.getWorkflowExecution(
        startDate,
        endDate
      );
      if (result.success && result.data.data) {
        processChartData(result.data.data);
      } else {
        setError("Failed to fetch workflow execution data");
      }
    } catch (err) {
      setError("Error fetching data: " + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (rawData: WorkflowExecutionDataPoint[]) => {
    const processed = rawData.map((item) => {
      const date = new Date(item.createdAt);
      const formattedDate = `${date.getMonth() + 1}/${date.getDate()}`;
      return {
        date: formattedDate,
        done: item.total_workflow_done,
        failed: item.total_workflow_failed,
        total: item.total_executions,
      };
    });
    setChartData(processed);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-4">
        <p className="font-bold text-gray-800 mb-2 text-sm">{data.date}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-xs text-gray-600">Done:</span>
            </div>
            <span className="text-sm font-bold text-emerald-600">
              {data.done}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-rose-500" />
              <span className="text-xs text-gray-600">Failed:</span>
            </div>
            <span className="text-sm font-bold text-rose-600">
              {data.failed}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 pt-1 border-t border-gray-200">
            <span className="text-xs text-gray-600 font-medium">Total:</span>
            <span className="text-sm font-bold text-gray-800">
              {data.total}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
      {/* Minimal Header */}
      <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Workflow Execution Trends
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Daily workflow completion analysis
            </p>
          </div>

          {/* Date Range Picker */}
          <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-3 py-2 shadow-sm">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 text-sm text-gray-700 focus:outline-none focus:ring-0 bg-transparent"
            />
            <span className="text-gray-400">—</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 text-sm text-gray-700 focus:outline-none focus:ring-0 bg-transparent"
            />
            <button
              onClick={fetchData}
              disabled={loading}
              className="ml-2 bg-gray-900 hover:bg-gray-800 text-white font-medium px-4 py-1 rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "..." : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-96">
            <Loader2 className="w-10 h-10 animate-spin text-gray-600 mb-3" />
            <p className="text-gray-600 text-sm">Loading workflow data...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-96 bg-red-50 rounded-lg border border-red-200">
            <AlertTriangle className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-red-600 font-semibold">{error}</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-80 bg-gray-50 rounded-lg">
            <Activity className="w-12 h-12 text-gray-400 mb-3" />
            <p className="text-gray-500 font-medium">
              No workflow execution data available
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Try selecting a different date range
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
            >
              <defs>
                <linearGradient id="colorDone" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                stroke="#9ca3af"
                style={{ fontSize: "12px", fontWeight: 500 }}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
              />

              <YAxis
                stroke="#9ca3af"
                style={{ fontSize: "12px", fontWeight: 500 }}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
              />

              <Tooltip content={<CustomTooltip />} />

              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{
                  fontSize: "13px",
                  fontWeight: 500,
                  paddingBottom: "10px",
                }}
              />

              <Area
                type="monotone"
                dataKey="done"
                stroke="#10b981"
                strokeWidth={2.5}
                fill="url(#colorDone)"
                name="Completed"
                animationDuration={800}
              />

              <Area
                type="monotone"
                dataKey="failed"
                stroke="#f43f5e"
                strokeWidth={2.5}
                fill="url(#colorFailed)"
                name="Failed"
                animationDuration={800}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default WorkflowExecutionChart;
